import {
  Button,
  Card,
  DatePicker,
  Form,
  message,
  Select,
  Table,
  Tag,
} from 'antd';
import LOCALS from 'commons/locals';
import { Trans } from 'react-i18next';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { useCallback, useMemo } from 'react';
import i18n from 'i18n';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
} from 'nuqs';
import dayjs from 'dayjs';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getProfitReport } from 'apis/oms';
import { Link } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import * as XLSX from 'xlsx';
import { findLabelByValue } from 'commons/options';
import formatTime from 'utils/formatTime';
import { STOCK_PLACE_OPTION_LIST } from 'constant/stock-place';
import {
  getOrderCreateSourceConfig,
  ORDER_CREATED_FROM_OPTION_LIST,
} from 'constant/order-create-source';
import { PRODUCT_SOURCE_TYPE_MAP } from 'constant/pms-product';
import { getOrderStatusConfig } from 'constant/oms-order-status';

const { RangePicker } = DatePicker;

type ProfitReportOmsOrder = Awaited<
  ReturnType<typeof getProfitReport>
>['list'][number];

// 扩展类型，添加 rowSpan 信息
type ProfitReportOrderWithRowSpan = ProfitReportOmsOrder & {
  _rowSpan?: {
    staffName?: number;
    totalSales?: number;
  };
  _staffTotalSales?: Record<string, number>; // 按币种聚合的总销售额
  _profit?: number; // 订单利润
};

// 统一底价口径：只要是 JPY 商品，底价都按 costPrice / 1.1 计算
const getEffectiveItemCost = (
  item: ProfitReportOmsOrder['orderItems'][number],
  currency: string,
): number => {
  const cost = (item.product?.costPrice ?? 0) * item.productQuantity;
  if (currency === 'JPY') {
    return Math.round(cost / 1.1);
  }
  return cost;
};

// 订单有效总成本（与利润计算一致，用于表格「总成本」列与 Excel 导出）
const getOrderEffectiveTotalCost = (
  orderItems: ProfitReportOmsOrder['orderItems'],
  currency: string,
): number => {
  if (!orderItems?.length) return 0;
  return orderItems.reduce(
    (acc, item) => acc + getEffectiveItemCost(item, currency),
    0,
  );
};

const getOrderIntegrationAmount = (order: ProfitReportOmsOrder): number => {
  const useIntegration = order.useIntegration || 0;
  if (!useIntegration) return 0;
  const rate = order.orderItems?.[0]?.actualCurrencyRate ?? 1;
  return Math.round(useIntegration * rate);
};

const getSourceTypeText = (sourceType?: number) => {
  if (sourceType === PRODUCT_SOURCE_TYPE_MAP.RECYCLE) return '回收';
  if (sourceType === PRODUCT_SOURCE_TYPE_MAP.CONSIGNMENT) return '寄卖';
  return '其他';
};

const getOrderSourceTypeText = (order: ProfitReportOrderWithRowSpan) => {
  const sourceTypes = Array.from(
    new Set(
      (order.orderItems || []).map((item) =>
        getSourceTypeText(item.product?.sourceType),
      ),
    ),
  );
  return sourceTypes.length ? sourceTypes.join('/') : '-';
};

const getOrderProductNameText = (order: ProfitReportOrderWithRowSpan) => {
  const names = (order.orderItems || [])
    .map((item) => {
      const anyItem = item as any;
      return (
        anyItem.product?.name ||
        anyItem.productName ||
        anyItem.product?.detailTitle ||
        anyItem.product?.title
      );
    })
    .filter(Boolean);
  if (!names.length) return '-';
  return names.join('\n');
};

const getOrderCustomerNameText = (order: ProfitReportOrderWithRowSpan) => {
  const firstName = order.member?.firstName?.trim();
  const lastName = order.member?.lastName?.trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  return '-';
};

const getOrderPaymentMethodList = (order: ProfitReportOrderWithRowSpan) => {
  const anyOrder = order as any;
  const paymentArray =
    anyOrder.orderPayList || anyOrder.payList || anyOrder.paymentList || [];
  if (Array.isArray(paymentArray) && paymentArray.length > 0) {
    return paymentArray
      .map((item: any) => {
        const type =
          item?.payType ||
          item?.payMode ||
          item?.paymentMethod ||
          item?.name ||
          item?.label;
        const amount = item?.payAmount;
        if (!type) return null;
        return {
          type: String(type),
          amount: typeof amount === 'number' ? amount : undefined,
        };
      })
      .filter(Boolean) as Array<{ type: string; amount?: number }>;
  }
  if (anyOrder.payType || anyOrder.payMode) {
    return [
      {
        type: String(anyOrder.payType || anyOrder.payMode),
      },
    ];
  }
  return [];
};

const getOrderPaymentMethodText = (order: ProfitReportOrderWithRowSpan) => {
  const list = getOrderPaymentMethodList(order);
  if (!list.length) return '-';
  return list
    .map((item) =>
      item.amount !== undefined
        ? `${item.type} (${item.amount.toLocaleString()})`
        : item.type,
    )
    .join('\n');
};

// 计算每个订单的利润：先算总成本价（JPY 商品底价按 costPrice / 1.1），再 利润 = 支付金额 - 总成本价 - 消费税
const calculateOrderProfit = (order: ProfitReportOmsOrder): number => {
  if (!order.orderItems || order.orderItems.length === 0) {
    return 0;
  }
  const payAmount = order.payAmountActualCurrency || 0;
  const taxAmount = order.totalTaxAmount || 0;
  const totalCostPrice = getOrderEffectiveTotalCost(
    order.orderItems,
    order.currency,
  );
  return payAmount - totalCostPrice - taxAmount;
};

const useProfitReportQueryParams = () => {
  const [staffList, setStaffList] = useQueryState(
    'staffList',
    parseAsArrayOf(parseAsString),
  );
  const [completeTime, setCompleteTime] = useQueryState(
    'completeTime',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [deliveryTime, setDeliveryTime] = useQueryState(
    'deliveryTime',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [stockPlaceList, setStockPlaceList] = useQueryState(
    'stockPlaceList',
    parseAsArrayOf(parseAsString),
  );
  const [createdFromList, setCreatedFromList] = useQueryState(
    'createdFromList',
    parseAsArrayOf(parseAsInteger),
  );

  return {
    staffList,
    setStaffList,
    completeTime,
    setCompleteTime,
    deliveryTime,
    setDeliveryTime,
    stockPlaceList,
    setStockPlaceList,
    createdFromList,
    setCreatedFromList,
  };
};

export const ProfitReport = () => {
  const { staffSelectOptions } = useAppSelector(selectGlobalInfo);
  const activeStaffSelectOptions = useMemo(() => {
    return staffSelectOptions.filter((i) => i.status === 1);
  }, [staffSelectOptions]);

  const {
    staffList,
    setStaffList,
    completeTime,
    setCompleteTime,
    deliveryTime,
    setDeliveryTime,
    stockPlaceList,
    setStockPlaceList,
    createdFromList,
    setCreatedFromList,
  } = useProfitReportQueryParams();

  const { orderStatusOptions } = useAppSelector(selectGlobalInfo);

  const { data, isFetching } = useQuery({
    // 将所有依赖参数放入 queryKey 数组
    queryKey: [
      'profitReport',
      staffList,
      completeTime,
      deliveryTime,
      stockPlaceList,
      createdFromList,
    ],
    queryFn: () =>
      getProfitReport({
        staffList: staffList || [],
        startCompleteTime: completeTime[0],
        endCompleteTime: completeTime[1],
        startDeliveryTime: deliveryTime[0],
        endDeliveryTime: deliveryTime[1],
        stockPlaceList: stockPlaceList || undefined,
        createdFromList: createdFromList || undefined,
      }),
    enabled: completeTime.length > 0 || deliveryTime.length > 0,
    placeholderData: keepPreviousData,
  });

  // 按担当者分组并计算总销售额
  const processedData = useMemo(() => {
    if (!data?.list) return [];

    const UNASSIGNED_STAFF_NAME = '-';

    // 按担当者分组
    const groupedByStaff = data.list.reduce(
      (acc, order) => {
        const staffName = order.staffName || UNASSIGNED_STAFF_NAME;
        if (!acc[staffName]) {
          acc[staffName] = [];
        }
        acc[staffName].push(order);
        return acc;
      },
      {} as Record<string, ProfitReportOmsOrder[]>,
    );

    // 计算每个担当者的总销售额（按币种）
    const staffTotals: Record<string, Record<string, number>> = {};
    Object.keys(groupedByStaff).forEach((staffName) => {
      const orders = groupedByStaff[staffName];
      const totals: Record<string, number> = {};
      orders.forEach((order) => {
        const currency = order.currency;
        if (!totals[currency]) {
          totals[currency] = 0;
        }
        totals[currency] += order.payAmountActualCurrency || 0;
      });
      staffTotals[staffName] = totals;
    });

    // 构建带 rowSpan 的数据
    // 将担当者名称排序，确保"-"（无担当者）放在最后
    const staffNames = Object.keys(groupedByStaff).sort((a, b) => {
      // 如果一个是"-"，另一个不是，"-"排在后面
      if (a === UNASSIGNED_STAFF_NAME && b !== UNASSIGNED_STAFF_NAME) return 1;
      if (a !== UNASSIGNED_STAFF_NAME && b === UNASSIGNED_STAFF_NAME) return -1;
      // 其他情况按字母顺序排序
      return a.localeCompare(b);
    });

    const result: ProfitReportOrderWithRowSpan[] = [];
    staffNames.forEach((staffName) => {
      const orders = groupedByStaff[staffName];
      orders.forEach((order, index) => {
        const orderWithRowSpan: ProfitReportOrderWithRowSpan = {
          ...order,
          _rowSpan: {
            staffName: index === 0 ? orders.length : 0,
            totalSales: index === 0 ? orders.length : 0,
          },
          _staffTotalSales: staffTotals[staffName],
          _profit: calculateOrderProfit(order),
        };
        result.push(orderWithRowSpan);
      });
    });

    return result;
  }, [data?.list]);

  // 计算所有订单的统计信息
  const totalStatistics = useMemo(() => {
    if (!data?.list || data.list.length === 0) {
      return {
        totalSales: {}, // 按币种的总销售额
        totalCost: {}, // 按币种的总底价（有效成本）
        totalProfit: {}, // 按币种的总毛利
        recycleSales: {}, // 回收商品销售额
        recycleCost: {}, // 回收商品底价
        recycleProfit: {}, // 回收商品毛利
        recycleItemCount: 0, // 回收件数
        consignmentSales: {}, // 寄卖商品销售额
        consignmentCost: {}, // 寄卖商品底价
        consignmentProfit: {}, // 寄卖商品毛利
        consignmentItemCount: 0, // 寄卖件数
        procurementSales: {}, // 采购商品销售额
        procurementProfit: {}, // 采购商品毛利
        totalUseIntegration: {}, // 按币种的总使用积分金额
        totalPromotionAmount: {}, // 按币种的总优惠金额
        totalOrderAmount: {}, // 按币种的总订单总金额
        totalPayAmount: {}, // 按币种的总应付金额
        b2bSales: {}, // B2B 订单总卖价（按币种，不含税）
      };
    }

    const stats = {
      totalSales: {} as Record<string, number>,
      totalCost: {} as Record<string, number>,
      totalProfit: {} as Record<string, number>,
      b2bSales: {} as Record<string, number>,
      recycleSales: {} as Record<string, number>,
      recycleCost: {} as Record<string, number>,
      recycleProfit: {} as Record<string, number>,
      recycleItemCount: 0,
      consignmentSales: {} as Record<string, number>,
      consignmentCost: {} as Record<string, number>,
      consignmentProfit: {} as Record<string, number>,
      consignmentItemCount: 0,
      procurementSales: {} as Record<string, number>,
      procurementProfit: {} as Record<string, number>,
      totalUseIntegration: {} as Record<string, number>,
      totalPromotionAmount: {} as Record<string, number>,
      totalOrderAmount: {} as Record<string, number>,
      totalPayAmount: {} as Record<string, number>,
    };

    data.list.forEach((order) => {
      const currency = order.currency;
      const profit = calculateOrderProfit(order);
      const payAmount = order.payAmountActualCurrency || 0;
      const totalTaxAmount = order.totalTaxAmount || 0;

      // 总销售额（应付金额）
      if (!stats.totalSales[currency]) {
        stats.totalSales[currency] = 0;
      }
      const salesWithoutTax = payAmount - totalTaxAmount;

      // 总卖价（不含税）
      stats.totalSales[currency] += salesWithoutTax;

      // B2B 订单总卖价（不含税）
      if (order.isB2B) {
        if (!stats.b2bSales[currency]) {
          stats.b2bSales[currency] = 0;
        }
        stats.b2bSales[currency] += salesWithoutTax;
      }

      // 总底价（有效成本，与利润计算口径保持一致）
      const orderCost = getOrderEffectiveTotalCost(order.orderItems, currency);
      if (!stats.totalCost[currency]) {
        stats.totalCost[currency] = 0;
      }
      stats.totalCost[currency] += orderCost;

      // 总毛利
      if (!stats.totalProfit[currency]) {
        stats.totalProfit[currency] = 0;
      }
      stats.totalProfit[currency] += profit;

      // 计算按商品来源类型拆分的销售额与毛利
      if (order.orderItems && order.orderItems.length > 0) {
        const itemTotals = order.orderItems.map(
          (item) =>
            (item.productPriceActualCurrency || 0) * item.productQuantity,
        );
        const orderItemsTotalAmount = itemTotals.reduce(
          (acc, val) => acc + val,
          0,
        );

        order.orderItems.forEach((item, index) => {
          const itemBaseAmount = itemTotals[index] || 0;
          if (!itemBaseAmount || !orderItemsTotalAmount) {
            return;
          }

          const ratio = itemBaseAmount / orderItemsTotalAmount;
          const itemSales = payAmount * ratio;
          const itemTax = totalTaxAmount * ratio;
          const sourceType = item.product?.sourceType;
          const itemSalesWithoutTax = itemSales - itemTax;
          const effectiveCost = getEffectiveItemCost(item, currency);
          // JPY 商品利润 = 客人实际支付金额 - (costPrice / 1.1) - 消费税
          const itemProfit = itemSales - effectiveCost - itemTax;

          if (sourceType === PRODUCT_SOURCE_TYPE_MAP.RECYCLE) {
            if (!stats.recycleSales[currency]) {
              stats.recycleSales[currency] = 0;
            }
            if (!stats.recycleCost[currency]) {
              stats.recycleCost[currency] = 0;
            }
            if (!stats.recycleProfit[currency]) {
              stats.recycleProfit[currency] = 0;
            }
            stats.recycleSales[currency] += itemSalesWithoutTax;
            stats.recycleCost[currency] += effectiveCost;
            stats.recycleProfit[currency] += itemProfit;
            stats.recycleItemCount += 1;
          } else if (sourceType === PRODUCT_SOURCE_TYPE_MAP.CONSIGNMENT) {
            if (!stats.consignmentSales[currency]) {
              stats.consignmentSales[currency] = 0;
            }
            if (!stats.consignmentCost[currency]) {
              stats.consignmentCost[currency] = 0;
            }
            if (!stats.consignmentProfit[currency]) {
              stats.consignmentProfit[currency] = 0;
            }
            stats.consignmentSales[currency] += itemSalesWithoutTax;
            stats.consignmentCost[currency] += effectiveCost;
            stats.consignmentProfit[currency] += itemProfit;
            stats.consignmentItemCount += 1;
          } else if (sourceType === PRODUCT_SOURCE_TYPE_MAP.PROCUREMENT) {
            if (!stats.procurementSales[currency]) {
              stats.procurementSales[currency] = 0;
            }
            if (!stats.procurementProfit[currency]) {
              stats.procurementProfit[currency] = 0;
            }
            stats.procurementSales[currency] += itemSalesWithoutTax;
            stats.procurementProfit[currency] += itemProfit;
          }
        });
      }

      // 总使用积分金额：积分数量 × 订单当时汇率，再按订单币种归类
      const integrationAmount = getOrderIntegrationAmount(order);
      if (integrationAmount) {
        if (!stats.totalUseIntegration[currency]) {
          stats.totalUseIntegration[currency] = 0;
        }
        stats.totalUseIntegration[currency] += integrationAmount;
      }

      // 总优惠金额
      if (order.promotionAmountActualCurrency) {
        if (!stats.totalPromotionAmount[currency]) {
          stats.totalPromotionAmount[currency] = 0;
        }
        stats.totalPromotionAmount[currency] +=
          order.promotionAmountActualCurrency;
      }

      // 总订单总金额
      if (!stats.totalOrderAmount[currency]) {
        stats.totalOrderAmount[currency] = 0;
      }
      stats.totalOrderAmount[currency] += order.totalAmountActualCurrency || 0;

      // 总应付金额
      if (!stats.totalPayAmount[currency]) {
        stats.totalPayAmount[currency] = 0;
      }
      stats.totalPayAmount[currency] += order.payAmountActualCurrency || 0;
    });

    return stats;
  }, [data?.list]);

  const columns = useMemo(() => {
    const columns: ColumnsType<ProfitReportOrderWithRowSpan> = [
      {
        title: i18n.t(LOCALS.staff),
        key: 'staffName',
        render: (
          { staffName }: ProfitReportOrderWithRowSpan,
          record: ProfitReportOrderWithRowSpan,
        ) => {
          const rowSpan = record._rowSpan?.staffName || 0;
          return {
            children: staffName || '-',
            props: {
              rowSpan: rowSpan,
            },
          };
        },
      },
      {
        title: '总销售额',
        key: 'totalSales',
        width: 200,
        render: (_: unknown, record: ProfitReportOrderWithRowSpan) => {
          const rowSpan = record._rowSpan?.totalSales || 0;
          const totals = record._staffTotalSales || {};
          const currencyKeys = Object.keys(totals).sort();

          const content =
            currencyKeys.length > 0 ? (
              <div>
                {currencyKeys.map((currency, index) => {
                  const amount = totals[currency];
                  return (
                    <div key={currency}>
                      {currency} {amount.toLocaleString()}
                    </div>
                  );
                })}
              </div>
            ) : (
              '-'
            );

          return {
            children: content,
            props: {
              rowSpan: rowSpan,
            },
          };
        },
      },
      {
        title: '订单ID',
        key: 'id',
        dataIndex: 'id',
        render: (id: ProfitReportOrderWithRowSpan['id']) => {
          return <Link to={`/oms/order-view/${id}`}>{id}</Link>;
        },
      },
      {
        title: '商品ID',
        key: 'productIds',
        width: 140,
        render: (record: ProfitReportOrderWithRowSpan) => {
          const productIds = record.orderItems?.map((d) => d.productId) || [];
          if (!productIds.length) return '-';

          return (
            <div className="flex flex-col">
              {productIds.map((productId, index) => (
                <div key={`${productId}_${index}`}>
                  <Link to={`/pms/product-edit/${productId}`}>{productId}</Link>
                </div>
              ))}
            </div>
          );
        },
      },
      {
        title: '商品名',
        key: 'productNames',
        width: 180,
        render: (record: ProfitReportOrderWithRowSpan) => {
          const productNameText = getOrderProductNameText(record);
          if (productNameText === '-') return '-';
          return (
            <div className="flex flex-col">
              {productNameText.split('\n').map((name, index) => (
                <div key={`${name}_${index}`}>{name}</div>
              ))}
            </div>
          );
        },
      },
      {
        title: '回收/寄卖/其他',
        key: 'sourceTypeText',
        width: 130,
        render: (record: ProfitReportOrderWithRowSpan) => {
          return getOrderSourceTypeText(record);
        },
      },
      {
        title: '客人姓名',
        key: 'customerName',
        width: 130,
        render: (record: ProfitReportOrderWithRowSpan) => {
          return getOrderCustomerNameText(record);
        },
      },
      {
        title: '付款方式',
        key: 'paymentMethodText',
        width: 170,
        render: (record: ProfitReportOrderWithRowSpan) => {
          const paymentList = getOrderPaymentMethodList(record);
          if (!paymentList.length) return '-';
          return (
            <div className="flex flex-col">
              {paymentList.map((item, index) => (
                <div key={`${item.type}_${item.amount ?? 'na'}_${index}`}>
                  {item.type}
                  {item.amount !== undefined
                    ? ` (${item.amount.toLocaleString()})`
                    : ''}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        title: i18n.t(LOCALS.order_total_amount),
        width: 150,
        key: 'totalAmountActualCurrency',
        render: ({
          totalAmountActualCurrency,
          currency,
        }: ProfitReportOrderWithRowSpan) => {
          return `${currency} ${totalAmountActualCurrency.toLocaleString()}`;
        },
      },
      {
        title: i18n.t(LOCALS.amount_to_be_paid),
        width: 150,
        key: 'payAmountActualCurrency',
        render: ({
          payAmountActualCurrency,
          currency,
        }: ProfitReportOrderWithRowSpan) => {
          return `${currency} ${payAmountActualCurrency.toLocaleString()}`;
        },
      },
      {
        title: i18n.t(LOCALS.tax_amount),
        key: 'totalTaxAmount',
        render: ({
          totalTaxAmount,
          currency,
        }: ProfitReportOrderWithRowSpan) => {
          return totalTaxAmount
            ? `${currency} ${totalTaxAmount.toLocaleString()}`
            : '-';
        },
      },
      {
        title: '总成本',
        key: 'totalCost',
        width: 150,
        render: (record: ProfitReportOrderWithRowSpan) => {
          if (!record.orderItems || record.orderItems.length === 0) {
            return '-';
          }
          const totalCostPrice = getOrderEffectiveTotalCost(
            record.orderItems,
            record.currency,
          );
          const currency = record.currency;
          return `${currency} ${totalCostPrice.toLocaleString()}`;
        },
      },
      {
        title: i18n.t(LOCALS.gross_profit),
        key: 'profit',
        width: 150,
        render: (record: ProfitReportOrderWithRowSpan) => {
          const profit = record._profit ?? 0;
          const currency = record.currency;
          return `${currency} ${profit.toLocaleString()}`;
        },
      },
      {
        title: i18n.t(LOCALS.points_deduction),
        key: 'useIntegration',
        render: (record: ProfitReportOrderWithRowSpan) => {
          const integrationAmount = getOrderIntegrationAmount(record);
          return integrationAmount
            ? `-${record.currency} ${integrationAmount.toLocaleString()}`
            : '-';
        },
      },
      {
        title: i18n.t(LOCALS.coupon),
        key: 'couponAmount',
        render: ({ couponAmount }: ProfitReportOrderWithRowSpan) => {
          return couponAmount ? `-JPY ${couponAmount.toLocaleString()}` : '-';
        },
      },
      {
        title: i18n.t(LOCALS.discount_amounts),
        key: 'promotionAmountActualCurrency',
        render: ({
          promotionAmountActualCurrency,
          currency,
        }: ProfitReportOrderWithRowSpan) => {
          return promotionAmountActualCurrency
            ? `-${currency} ${promotionAmountActualCurrency.toLocaleString()}`
            : '-';
        },
      },
      {
        title: i18n.t(LOCALS.freight),
        key: 'freightAmountActualCurrency',
        render: ({
          freightAmountActualCurrency,
          currency,
        }: ProfitReportOrderWithRowSpan) => {
          return freightAmountActualCurrency
            ? `${currency} ${freightAmountActualCurrency.toLocaleString()}`
            : '-';
        },
      },
      {
        title: i18n.t(LOCALS.email),
        key: 'memberId',
        render: ({
          memberId,
          memberUsername,
        }: ProfitReportOrderWithRowSpan) => {
          if (!memberId) return '-';
          return (
            <Link to={`/ums/member-view/${memberId}`}>{memberUsername}</Link>
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.order_status}></Trans>,
        dataIndex: 'status',
        key: 'status',
        render: (status: ProfitReportOrderWithRowSpan['status']) => {
          return (
            <Tag color={getOrderStatusConfig(status).color}>
              {findLabelByValue(status, orderStatusOptions)}
            </Tag>
          );
        },
      },

      {
        title: <Trans i18nKey={LOCALS.created_from}></Trans>,
        dataIndex: 'createdFrom',
        key: 'createdFrom',
        render: (createdFrom: ProfitReportOrderWithRowSpan['createdFrom']) => {
          return findLabelByValue(createdFrom, ORDER_CREATED_FROM_OPTION_LIST);
        },
      },
      {
        title: 'B2B',
        dataIndex: 'isB2B',
        key: 'isB2B',
        width: 90,
        align: 'center',
        render: (isB2B: ProfitReportOrderWithRowSpan['isB2B']) => {
          if (isB2B === undefined || isB2B === null) return '-';
          return isB2B ? <Tag color="red">B2B</Tag> : '-';
        },
      },
      {
        title: <Trans i18nKey={LOCALS.stock_place}></Trans>,
        key: 'stockPlace',
        render: ({ orderItems }: ProfitReportOrderWithRowSpan) => {
          if (!orderItems.length) return '-';
          const { stockPlace } = orderItems[0].product;
          return findLabelByValue(stockPlace, STOCK_PLACE_OPTION_LIST);
        },
      },
      {
        title: <Trans i18nKey={LOCALS.created_time}></Trans>,
        dataIndex: 'createTime',
        key: 'createTime',
        render: (createTime: ProfitReportOrderWithRowSpan['createTime']) => {
          return formatTime(createTime);
        },
      },
      {
        title: <Trans i18nKey={LOCALS.payment_time}></Trans>,
        dataIndex: 'paymentTime',
        key: 'paymentTime',
        render: (paymentTime: ProfitReportOrderWithRowSpan['paymentTime']) => {
          return formatTime(paymentTime);
        },
      },
      {
        title: <Trans i18nKey={LOCALS.delivery_time}></Trans>,
        dataIndex: 'deliveryTime',
        key: 'deliveryTime',
        render: (
          deliveryTime: ProfitReportOrderWithRowSpan['deliveryTime'],
        ) => {
          return formatTime(deliveryTime);
        },
      },
      {
        title: <Trans i18nKey={LOCALS.complete_time}></Trans>,
        dataIndex: 'receiveTime',
        key: 'receiveTime',
        render: (receiveTime: ProfitReportOrderWithRowSpan['receiveTime']) => {
          return formatTime(receiveTime);
        },
      },
    ];

    return columns;
  }, [orderStatusOptions]);

  const handleDownloadExcel = useCallback(() => {
    if (!processedData.length) {
      message.warning('没有可下载的数据');
      return;
    }

    const getCreatedFromText = (createdFrom?: number) => {
      if (!createdFrom) return '-';
      return getOrderCreateSourceConfig(createdFrom).label;
    };

    const getStockPlaceText = (stockPlace?: string) => {
      return findLabelByValue(stockPlace, STOCK_PLACE_OPTION_LIST);
    };

    // 表头顺序完全参照页面表格
    const header = [
      i18n.t(LOCALS.staff),
      '总销售额',
      '订单ID',
      '商品ID',
      '商品名',
      '回收/寄卖/其他',
      '客人姓名',
      '付款方式',
      i18n.t(LOCALS.order_total_amount),
      i18n.t(LOCALS.amount_to_be_paid),
      i18n.t(LOCALS.tax_amount),
      '总成本',
      i18n.t(LOCALS.gross_profit),
      i18n.t(LOCALS.points_deduction),
      i18n.t(LOCALS.coupon),
      i18n.t(LOCALS.discount_amounts),
      i18n.t(LOCALS.freight),
      i18n.t(LOCALS.email),
      i18n.t(LOCALS.order_status),
      i18n.t(LOCALS.created_from),
      'B2B',
      i18n.t(LOCALS.stock_place),
      i18n.t(LOCALS.created_time),
      i18n.t(LOCALS.payment_time),
      i18n.t(LOCALS.delivery_time),
      i18n.t(LOCALS.complete_time),
    ];

    const aoa: string[][] = [header];

    // 合并单元格（担当者、总销售额）参照 Table 的 rowSpan 逻辑
    const merges: XLSX.Range[] = [];

    processedData.forEach((record, index) => {
      const rowIndex = index + 1; // 0 是 header

      const staffRowSpan = record._rowSpan?.staffName || 0;
      const totalSalesRowSpan = record._rowSpan?.totalSales || 0;

      const staffName = staffRowSpan > 0 ? record.staffName || '-' : '';

      const totals = record._staffTotalSales || {};
      const currencyKeys = Object.keys(totals).sort();
      const totalSalesText =
        totalSalesRowSpan > 0
          ? currencyKeys.length
            ? currencyKeys
                .map(
                  (currency) =>
                    `${currency} ${totals[currency].toLocaleString()}`,
                )
                .join('\n')
            : '-'
          : '';

      const orderId = record.id;
      const productIds = record.orderItems?.map((d) => d.productId) || [];
      const productIdsText = productIds.length ? productIds.join('\n') : '-';
      const productNamesText = getOrderProductNameText(record);
      const sourceTypeText = getOrderSourceTypeText(record);
      const customerNameText = getOrderCustomerNameText(record);
      const paymentMethodText = getOrderPaymentMethodText(record);

      const totalAmountText = `${
        record.currency
      } ${record.totalAmountActualCurrency.toLocaleString()}`;
      const payAmountText = `${
        record.currency
      } ${record.payAmountActualCurrency.toLocaleString()}`;
      const taxText = record.totalTaxAmount
        ? `${record.currency} ${record.totalTaxAmount.toLocaleString()}`
        : '-';

      const totalCostPrice = getOrderEffectiveTotalCost(
        record.orderItems ?? [],
        record.currency,
      );
      const totalCostText =
        record.orderItems && record.orderItems.length
          ? `${record.currency} ${totalCostPrice.toLocaleString()}`
          : '-';

      const profit = record._profit ?? 0;
      const profitText = `${record.currency} ${profit.toLocaleString()}`;

      const integrationAmount = getOrderIntegrationAmount(record);
      const useIntegrationText = integrationAmount
        ? `-${record.currency} ${integrationAmount.toLocaleString()}`
        : '-';
      const couponText = record.couponAmount
        ? `-JPY ${record.couponAmount.toLocaleString()}`
        : '-';
      const promotionText = record.promotionAmountActualCurrency
        ? `-${
            record.currency
          } ${record.promotionAmountActualCurrency.toLocaleString()}`
        : '-';
      const freightText = record.freightAmountActualCurrency
        ? `${
            record.currency
          } ${record.freightAmountActualCurrency.toLocaleString()}`
        : '-';

      const emailText = record.memberId ? record.memberUsername || '-' : '-';
      const statusText =
        findLabelByValue(record.status, orderStatusOptions) || '-';
      const createdFromText = getCreatedFromText(record.createdFrom);
      const isB2BText = record.isB2B ? 'B2B' : '-';

      const stockPlace = record.orderItems?.[0]?.product?.stockPlace;
      const stockPlaceText = getStockPlaceText(stockPlace);

      const createTimeText = formatTime(record.createTime);
      const paymentTimeText = formatTime(record.paymentTime);
      const deliveryTimeText = formatTime(record.deliveryTime);
      const completeTimeText = formatTime(record.receiveTime);

      aoa.push([
        staffName,
        totalSalesText,
        orderId,
        productIdsText,
        productNamesText,
        sourceTypeText,
        customerNameText,
        paymentMethodText,
        totalAmountText,
        payAmountText,
        taxText,
        totalCostText,
        profitText,
        useIntegrationText,
        couponText,
        promotionText,
        freightText,
        emailText,
        statusText,
        createdFromText,
        isB2BText,
        stockPlaceText,
        createTimeText,
        paymentTimeText,
        deliveryTimeText,
        completeTimeText,
      ]);

      if (staffRowSpan > 1) {
        merges.push({
          s: { r: rowIndex, c: 0 },
          e: { r: rowIndex + staffRowSpan - 1, c: 0 },
        });
      }
      if (totalSalesRowSpan > 1) {
        merges.push({
          s: { r: rowIndex, c: 1 },
          e: { r: rowIndex + totalSalesRowSpan - 1, c: 1 },
        });
      }
    });

    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    worksheet['!merges'] = merges;
    worksheet['!cols'] = [
      { wch: 12 }, // 担当者
      { wch: 22 }, // 总销售额
      { wch: 10 }, // 订单ID
      { wch: 12 }, // 商品ID
      { wch: 24 }, // 商品名
      { wch: 14 }, // 回收/寄卖/其他
      { wch: 16 }, // 客人姓名
      { wch: 20 }, // 付款方式
      { wch: 16 }, // 订单总金额
      { wch: 16 }, // 应付金额
      { wch: 14 }, // 消费税额
      { wch: 14 }, // 总成本
      { wch: 14 }, // 毛利
      { wch: 12 }, // 积分抵扣
      { wch: 10 }, // 优惠券
      { wch: 12 }, // 优惠金额
      { wch: 10 }, // 运费
      { wch: 20 }, // 电子邮箱
      { wch: 12 }, // 订单状态
      { wch: 12 }, // 创建来源
      { wch: 6 }, // B2B
      { wch: 10 }, // 库存地
      { wch: 20 }, // 创建时间
      { wch: 20 }, // 支付时间
      { wch: 20 }, // 发货时间
      { wch: 20 }, // 完成时间
    ];

    // 让包含换行符的单元格在 Excel 里直接显示为多行
    // 主要处理：总销售额（B列）/ 商品ID（D列）/ 商品名（E列）
    const rows: { hpt?: number }[] = [];
    rows[0] = { hpt: 18 }; // header

    const applyWrapText = (r: number, c: number) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = worksheet[addr];
      if (!cell) return;
      cell.s = {
        ...(cell.s || {}),
        alignment: {
          ...((cell.s && cell.s.alignment) || {}),
          wrapText: true,
          vertical: 'top',
        },
      };
    };

    for (let r = 1; r < aoa.length; r++) {
      // B列(1) + D列(3) + E列(4) 开启自动换行
      applyWrapText(r, 1);
      applyWrapText(r, 3);
      applyWrapText(r, 4);

      const b = String(aoa[r]?.[1] ?? '');
      const d = String(aoa[r]?.[3] ?? '');
      const e = String(aoa[r]?.[4] ?? '');
      const lineCountB = b ? b.split('\n').length : 1;
      const lineCountD = d ? d.split('\n').length : 1;
      const lineCountE = e ? e.split('\n').length : 1;
      const maxLines = Math.max(1, lineCountB, lineCountD, lineCountE);

      // 控制一下行高上限，避免超多行把表拉得太高
      const capped = Math.min(maxLines, 8);
      rows[r] = { hpt: 15 * capped };
    }

    worksheet['!rows'] = rows;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '利润报表');

    // 添加统计信息 sheet
    const formatCurrencyAmountLines = (
      data: Record<string, number>,
      prefix = '',
    ) => {
      return Object.keys(data)
        .sort()
        .map((currency) => {
          const amount = data[currency].toLocaleString();
          return `${prefix}${currency} ${amount}`;
        })
        .join('\n');
    };

    const statsAoa: string[][] = [
      [
        '总卖价',
        '总底价',
        '总利润',
        'B2B总卖价',
        '寄卖总卖价',
        '寄卖总底价',
        '寄卖总利润',
        '寄卖',
        '自社采购总卖价',
        '总使用积分',
      ],
      [
        formatCurrencyAmountLines(totalStatistics.totalSales),
        formatCurrencyAmountLines(totalStatistics.totalCost),
        formatCurrencyAmountLines(totalStatistics.totalProfit),
        formatCurrencyAmountLines(totalStatistics.b2bSales),
        formatCurrencyAmountLines(totalStatistics.consignmentSales),
        formatCurrencyAmountLines(totalStatistics.consignmentCost),
        formatCurrencyAmountLines(totalStatistics.consignmentProfit),
        String(totalStatistics.consignmentItemCount || 0),
        formatCurrencyAmountLines(totalStatistics.procurementSales),
        formatCurrencyAmountLines(totalStatistics.totalUseIntegration, '-'),
      ],
      [
        '',
        '',
        '',
        '',
        '回收总卖价',
        '回收总底价',
        '回收总利润',
        '回收',
        '自社采购总利润',
        '总优惠金额',
      ],
      [
        '',
        '',
        '',
        '',
        formatCurrencyAmountLines(totalStatistics.recycleSales),
        formatCurrencyAmountLines(totalStatistics.recycleCost),
        formatCurrencyAmountLines(totalStatistics.recycleProfit),
        String(totalStatistics.recycleItemCount || 0),
        formatCurrencyAmountLines(totalStatistics.procurementProfit),
        formatCurrencyAmountLines(totalStatistics.totalPromotionAmount, '-'),
      ],
    ];

    const statsWorksheet = XLSX.utils.aoa_to_sheet(statsAoa);
    statsWorksheet['!merges'] = [
      { s: { r: 1, c: 0 }, e: { r: 3, c: 0 } },
      { s: { r: 1, c: 1 }, e: { r: 3, c: 1 } },
      { s: { r: 1, c: 2 }, e: { r: 3, c: 2 } },
      { s: { r: 1, c: 3 }, e: { r: 3, c: 3 } },
    ];
    statsWorksheet['!cols'] = [
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 8 },
      { wch: 16 },
      { wch: 16 },
    ];
    // 显式收敛使用区域，避免某些情况下 Excel 认为范围更大
    statsWorksheet['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: statsAoa.length - 1, c: 9 },
    });
    // 默认关闭网格线，避免看起来有"多余空行"
    // SheetJS: !sheetViews -> showGridLines
    // @ts-ignore SheetJS 扩展字段
    statsWorksheet['!sheetViews'] = [{ showGridLines: false }];

    // 开启自动换行 + 行高适配（多列都可能多行）
    const statsRows: { hpt?: number }[] = [];
    statsRows[0] = { hpt: 18 };
    const applyStatsWrap = (r: number, c: number) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = statsWorksheet[addr];
      if (!cell) return;
      cell.s = {
        ...(cell.s || {}),
        alignment: {
          ...((cell.s && cell.s.alignment) || {}),
          wrapText: true,
          vertical: 'top',
        },
      };
    };
    for (let r = 1; r < statsAoa.length; r++) {
      let maxLines = 1;
      for (let c = 0; c <= 9; c++) {
        applyStatsWrap(r, c);
        const text = String(statsAoa[r]?.[c] ?? '');
        const lineCount = text ? text.split('\n').length : 1;
        maxLines = Math.max(maxLines, lineCount);
      }
      const capped = Math.min(Math.max(1, maxLines), 12);
      statsRows[r] = { hpt: 15 * capped };
    }
    // 先截断数组，确保长度严格等于数据行数，再赋值
    statsRows.length = statsAoa.length;
    statsWorksheet['!rows'] = statsRows;

    // 给统计表区域加边框（网格线关闭后仍保留表格形态）
    const statsBorder = {
      top: { style: 'thin', color: { rgb: 'D9D9D9' } },
      bottom: { style: 'thin', color: { rgb: 'D9D9D9' } },
      left: { style: 'thin', color: { rgb: 'D9D9D9' } },
      right: { style: 'thin', color: { rgb: 'D9D9D9' } },
    } as const;
    for (let r = 0; r < statsAoa.length; r++) {
      for (let c = 0; c <= 9; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = statsWorksheet[addr];
        if (!cell) continue;
        cell.s = {
          ...(cell.s || {}),
          border: statsBorder,
          alignment: {
            ...((cell.s && cell.s.alignment) || {}),
            vertical: 'center',
            horizontal: 'center',
            wrapText: true,
          },
          fill:
            r === 0 || r === 2
              ? {
                  patternType: 'solid',
                  fgColor: { rgb: 'E6E6E6' },
                }
              : (cell.s && (cell.s as any).fill) || undefined,
          font:
            r === 0 || r === 2
              ? { ...((cell.s && cell.s.font) || {}), bold: true }
              : (cell.s && cell.s.font) || {},
        };
      }
    }

    // 手动清理：删除所有超出数据范围的单元格引用（避免 Excel 显示额外空行）
    const maxRow = statsAoa.length - 1;
    const maxCol = 9;
    Object.keys(statsWorksheet).forEach((key) => {
      if (key[0] === '!') return; // 跳过特殊属性
      const cellRef = XLSX.utils.decode_cell(key);
      if (cellRef.r > maxRow || cellRef.c > maxCol) {
        delete statsWorksheet[key];
      }
    });

    // 强制截断 !rows 数组，确保长度与数据行一致
    if (statsWorksheet['!rows']) {
      statsWorksheet['!rows'].length = statsAoa.length;
    }

    // 最后再次强制设置 !ref，确保 Excel 知道真实范围
    statsWorksheet['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: statsAoa.length - 1, c: 8 },
    });

    // 设置 worksheet 维度信息（有些 Excel 版本会读取这个）
    // @ts-ignore
    statsWorksheet['!dimension'] = statsWorksheet['!ref'];

    XLSX.utils.book_append_sheet(workbook, statsWorksheet, '统计信息');

    // 设置打印区域，让导出的统计表只覆盖真实数据范围（避免打印/导出 PDF 时出现多余空白）
    const statsPrintArea = `'统计信息'!$A$1:$J$${statsAoa.length}`;
    // SheetJS 允许通过定义名称 _xlnm.Print_Area 设置打印区域
    // 这里不覆盖已有 Names，做增量追加
    // @ts-ignore SheetJS workbook 扩展字段
    const wbAny = workbook as any;
    if (!wbAny.Workbook) wbAny.Workbook = {};
    if (!wbAny.Workbook.Names) wbAny.Workbook.Names = [];
    wbAny.Workbook.Names.push({
      Name: '_xlnm.Print_Area',
      Ref: statsPrintArea,
      Sheet: 1, // 第二个 sheet（0-based）
    });

    const filename = `利润报表_${dayjs().format('YYYY-MM-DD')}.xlsx`;
    XLSX.writeFile(workbook, filename, { cellStyles: true });
  }, [orderStatusOptions, processedData, totalStatistics]);

  const formatCurrencyLines = (
    data: Record<string, number>,
    options?: { prefix?: string },
  ) => {
    const keys = Object.keys(data).sort();
    if (!keys.length) return '-';
    const prefix = options?.prefix || '';
    return (
      <div>
        {keys.map((currency) => (
          <div key={currency} style={{ lineHeight: 1.7 }}>
            {prefix}
            {currency} {data[currency].toLocaleString()}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div className="space-y-4">
        <Card bodyStyle={{ padding: '16px' }} title={'利润报表'}>
          {/* 统计信息移动到页面顶部（按目标图表样式展示） */}
          {processedData.length > 0 && (
            <div
              style={{
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: '#fafafa',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '12px' }}>
                统计信息
              </div>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  tableLayout: 'fixed',
                  fontSize: 14,
                }}
              >
                <thead>
                  <tr>
                    {[
                      '总卖价',
                      '总底价',
                      '总利润',
                      'B2B总卖价',
                      '寄卖总卖价',
                      '寄卖总底价',
                      '寄卖总利润',
                      '寄卖',
                      '自社采购总卖价',
                      '总使用积分',
                    ].map((title) => (
                      <th
                        key={title}
                        style={{
                          border: '1px solid #666',
                          background: '#e6e6e6',
                          padding: '6px 8px',
                          textAlign: 'center',
                          fontWeight: 600,
                        }}
                      >
                        {title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      rowSpan={3}
                      style={{
                        border: '1px solid #666',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        padding: '6px 8px',
                      }}
                    >
                      {formatCurrencyLines(totalStatistics.totalSales)}
                    </td>
                    <td
                      rowSpan={3}
                      style={{
                        border: '1px solid #666',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        padding: '6px 8px',
                      }}
                    >
                      {formatCurrencyLines(totalStatistics.totalCost)}
                    </td>
                    <td
                      rowSpan={3}
                      style={{
                        border: '1px solid #666',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        padding: '6px 8px',
                      }}
                    >
                      {formatCurrencyLines(totalStatistics.totalProfit)}
                    </td>
                    <td
                      rowSpan={3}
                      style={{
                        border: '1px solid #666',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        padding: '6px 8px',
                      }}
                    >
                      {formatCurrencyLines(totalStatistics.b2bSales)}
                    </td>
                    <td
                      style={{ border: '1px solid #666', textAlign: 'center' }}
                    >
                      {formatCurrencyLines(totalStatistics.consignmentSales)}
                    </td>
                    <td
                      style={{ border: '1px solid #666', textAlign: 'center' }}
                    >
                      {formatCurrencyLines(totalStatistics.consignmentCost)}
                    </td>
                    <td
                      style={{ border: '1px solid #666', textAlign: 'center' }}
                    >
                      {formatCurrencyLines(totalStatistics.consignmentProfit)}
                    </td>
                    <td
                      style={{
                        border: '1px solid #666',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                      }}
                    >
                      {totalStatistics.consignmentItemCount}
                    </td>
                    <td
                      style={{ border: '1px solid #666', textAlign: 'center' }}
                    >
                      {formatCurrencyLines(totalStatistics.procurementSales)}
                    </td>
                    <td
                      style={{
                        border: '1px solid #666',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                      }}
                    >
                      {formatCurrencyLines(
                        totalStatistics.totalUseIntegration,
                        {
                          prefix: '-',
                        },
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        border: '1px solid #666',
                        background: '#e6e6e6',
                        textAlign: 'center',
                        fontWeight: 600,
                        padding: '4px 8px',
                      }}
                    >
                      回收总卖价
                    </td>
                    <td
                      style={{
                        border: '1px solid #666',
                        background: '#e6e6e6',
                        textAlign: 'center',
                        fontWeight: 600,
                        padding: '4px 8px',
                      }}
                    >
                      回收总底价
                    </td>
                    <td
                      style={{
                        border: '1px solid #666',
                        background: '#e6e6e6',
                        textAlign: 'center',
                        fontWeight: 600,
                        padding: '4px 8px',
                      }}
                    >
                      回收总利润
                    </td>
                    <td
                      style={{
                        border: '1px solid #666',
                        background: '#e6e6e6',
                        textAlign: 'center',
                        fontWeight: 600,
                        padding: '4px 8px',
                      }}
                    >
                      回收
                    </td>
                    <td
                      style={{
                        border: '1px solid #666',
                        background: '#e6e6e6',
                        textAlign: 'center',
                        fontWeight: 600,
                        padding: '4px 8px',
                      }}
                    >
                      自社采购总利润
                    </td>
                    <td
                      style={{
                        border: '1px solid #666',
                        background: '#e6e6e6',
                        textAlign: 'center',
                        fontWeight: 600,
                        padding: '4px 8px',
                      }}
                    >
                      总优惠金额
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{ border: '1px solid #666', textAlign: 'center' }}
                    >
                      {formatCurrencyLines(totalStatistics.recycleSales)}
                    </td>
                    <td
                      style={{ border: '1px solid #666', textAlign: 'center' }}
                    >
                      {formatCurrencyLines(totalStatistics.recycleCost)}
                    </td>
                    <td
                      style={{ border: '1px solid #666', textAlign: 'center' }}
                    >
                      {formatCurrencyLines(totalStatistics.recycleProfit)}
                    </td>
                    <td
                      style={{
                        border: '1px solid #666',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                      }}
                    >
                      {totalStatistics.recycleItemCount}
                    </td>
                    <td
                      style={{ border: '1px solid #666', textAlign: 'center' }}
                    >
                      {formatCurrencyLines(totalStatistics.procurementProfit)}
                    </td>
                    <td
                      style={{ border: '1px solid #666', textAlign: 'center' }}
                    >
                      {formatCurrencyLines(
                        totalStatistics.totalPromotionAmount,
                        {
                          prefix: '-',
                        },
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <Form layout={'inline'}>
            <Form.Item
              label={<Trans i18nKey={LOCALS.staff} />}
              className="w-52"
            >
              <Select
                placeholder={i18n.t(LOCALS.please_select)}
                allowClear
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                showSearch
                value={staffList || undefined}
                onChange={(value) => {
                  if (!value.length) {
                    setStaffList(null);
                  } else {
                    setStaffList(value);
                  }
                }}
                mode="multiple"
                options={activeStaffSelectOptions.map((i) => ({
                  label: i.label,
                  value: i.label,
                }))}
              />
            </Form.Item>

            <Form.Item
              label={<Trans i18nKey={LOCALS.stock_place} />}
              className="w-60"
            >
              <Select
                placeholder={i18n.t(LOCALS.please_select)}
                value={stockPlaceList || undefined}
                mode="multiple"
                allowClear
                showSearch
                onChange={(value) => {
                  if (!value.length) {
                    setStockPlaceList(null);
                  } else {
                    setStockPlaceList(value);
                  }
                }}
              >
                {STOCK_PLACE_OPTION_LIST.map((i) => (
                  <Select.Option key={i.value} value={i.value}>
                    {i.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={<Trans i18nKey={LOCALS.created_from} />}
              className="w-60"
            >
              <Select
                placeholder={i18n.t(LOCALS.please_select)}
                value={createdFromList || undefined}
                mode="multiple"
                allowClear
                showSearch
                onChange={(value) => {
                  if (!value.length) {
                    setCreatedFromList(null);
                  } else {
                    setCreatedFromList(value);
                  }
                }}
              >
                {ORDER_CREATED_FROM_OPTION_LIST.map((i) => (
                  <Select.Option key={i.value} value={i.value}>
                    {i.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label={<Trans i18nKey={LOCALS.delivery_time} />}>
              <RangePicker
                value={
                  deliveryTime.length === 2
                    ? [dayjs(deliveryTime[0]), dayjs(deliveryTime[1])]
                    : null
                }
                onChange={(dates) => {
                  if (dates) {
                    setDeliveryTime([
                      dayjs(dates[0]).startOf('days').format() || '',
                      dayjs(dates[1]).endOf('days').format() || '',
                    ]);
                  } else {
                    setDeliveryTime([]);
                  }
                }}
              />
            </Form.Item>

            <Form.Item label={<Trans i18nKey={LOCALS.completion_time} />}>
              <RangePicker
                value={
                  completeTime.length === 2
                    ? [dayjs(completeTime[0]), dayjs(completeTime[1])]
                    : null
                }
                onChange={(dates) => {
                  if (dates) {
                    setCompleteTime([
                      dayjs(dates[0]).startOf('days').format() || '',
                      dayjs(dates[1]).endOf('days').format() || '',
                    ]);
                  } else {
                    setCompleteTime([]);
                  }
                }}
              />
            </Form.Item>

            <Form.Item>
              <div className="flex gap-2">
                <Button
                  htmlType="reset"
                  onClick={() => {
                    setStaffList(null);
                    setStockPlaceList(null);
                    setCreatedFromList(null);
                    setDeliveryTime(null);
                    setCompleteTime(null);
                  }}
                >
                  <Trans i18nKey={LOCALS.reset} />
                </Button>
                <Button type="primary" onClick={handleDownloadExcel}>
                  下载 Excel
                </Button>
              </div>
            </Form.Item>
          </Form>

          <Table
            dataSource={processedData}
            columns={columns}
            bordered
            loading={isFetching}
            rowKey={'id'}
            scroll={{ x: 'max-content' }}
            pagination={false}
          />
        </Card>
        {/* <Card bodyStyle={{ padding: '16px' }}></Card> */}
      </div>
    </div>
  );
};
